import { Fragment, ReactNode } from "react";

import { Popover, PopoverContent, PopoverTrigger } from "../../base/popover";

/**
 * Renders a list of values (already-rendered nodes, e.g. links) on a single
 * line: the first `maxInline` shown inline, the remainder behind a "+N" badge
 * that opens a popover listing the hidden values, one per line. Keeps table
 * rows a uniform height when a cell can hold many values, while keeping each
 * value clickable and the popover as narrow as its widest item.
 */
export function CellOverflowList({
  items,
  maxInline = 3,
  separator = ", ",
}: {
  items: ReactNode[];
  maxInline?: number;
  separator?: ReactNode;
}) {
  if (items.length === 0) return null;

  const inline = items.slice(0, maxInline);
  const overflow = items.slice(maxInline);

  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <span className="truncate">
        {inline.map((node, index) => (
          <Fragment key={index}>
            {index > 0 && separator}
            {node}
          </Fragment>
        ))}
        {overflow.length > 0 && separator}
      </span>

      {overflow.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              onClick={(event) => event.stopPropagation()}
              className="text-brand-primary hover:text-brand-primary/80 inline-flex shrink-0 cursor-pointer items-center bg-transparent p-0 text-[11px] font-medium"
            >
              +{overflow.length}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="center"
            className="w-auto max-w-xs"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex flex-col gap-y-1.5">
              {overflow.map((node, index) => (
                <span key={index}>{node}</span>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </span>
  );
}
